import { useState } from 'react';
import { format, parseISO, differenceInDays } from 'date-fns';
import {
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Clock,
  ChevronDown,
  ChevronRight,
  Calendar,
  TrendingDown,
} from 'lucide-react';
import {
  useAtRiskProjects,
  getStatusBadgeClass,
  formatVariance,
  type Project,
  type ProjectStatus,
} from '../hooks/useProjects';

/**
 * At-Risk Projects page - displays projects where expected > promised.
 */
export function AtRisk() {
  const { data, isLoading, isError, error, refetch, isFetching } = useAtRiskProjects();
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (isLoading) {
    return <AtRiskLoading />;
  }

  if (isError) {
    return <AtRiskError message={error.message} onRetry={() => refetch()} />;
  }

  const projects = data?.projects ?? [];
  const summary = data?.summary;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">At-Risk Projects</h2>
          <p className="text-sm text-foreground-muted mt-1">
            Projects where expected delivery exceeds promised date
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-background-subtle transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      {summary && <SummaryCards summary={summary} />}

      {projects.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-4">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              isExpanded={expandedProjects.has(project.id)}
              onToggle={() => toggleExpanded(project.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCards({ summary }: { summary: { total: number; onTrack: number; atRisk: number; late: number } }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <SummaryCard
        label="Total Projects"
        value={summary.total}
        icon={Calendar}
        color="text-foreground"
      />
      <SummaryCard
        label="On Track"
        value={summary.onTrack}
        icon={Clock}
        color="text-success"
      />
      <SummaryCard
        label="At Risk"
        value={summary.atRisk}
        icon={AlertTriangle}
        color="text-warning"
      />
      <SummaryCard
        label="Late"
        value={summary.late}
        icon={AlertCircle}
        color="text-danger"
      />
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  icon: typeof Clock;
  color: string;
}) {
  return (
    <div className="border border-border rounded-lg p-4 bg-background-subtle">
      <div className="flex items-center justify-between">
        <span className="text-sm text-foreground-muted">{label}</span>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <div className={`text-2xl font-semibold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function ProjectCard({
  project,
  isExpanded,
  onToggle,
}: {
  project: Project;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const progress = project.tasksTotal
    ? Math.round((project.tasksComplete ?? 0 / project.tasksTotal) * 100)
    : 0;

  return (
    <div className="border border-border rounded-lg bg-background overflow-hidden">
      {/* Main row */}
      <div
        className="p-4 cursor-pointer hover:bg-background-subtle transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button className="text-foreground-muted hover:text-foreground">
              {isExpanded ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </button>

            <div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-medium">
                  {project.gpSalesOrderNumber}
                </span>
                <StatusBadge status={project.status} />
              </div>
              <div className="text-sm text-foreground-muted mt-0.5">
                {project.customerName}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-8">
            {/* Dates */}
            <div className="text-right">
              <div className="text-sm">
                <span className="text-foreground-muted">Promised: </span>
                <span>{formatDate(project.latestPromisedShipDate)}</span>
              </div>
              <div className="text-sm">
                <span className="text-foreground-muted">Expected: </span>
                <span className={project.daysVariance < 0 ? 'text-danger font-medium' : ''}>
                  {project.expectedShipDate ? formatDate(project.expectedShipDate) : '-'}
                </span>
              </div>
            </div>

            {/* Variance indicator */}
            <VarianceIndicator days={project.daysVariance} />
          </div>
        </div>

        {/* Progress bar */}
        {project.tasksTotal && (
          <div className="mt-3 ml-9">
            <div className="flex items-center justify-between text-xs text-foreground-muted mb-1">
              <span>Progress</span>
              <span>
                {project.tasksComplete ?? 0} / {project.tasksTotal} tasks
              </span>
            </div>
            <div className="h-2 bg-background-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="border-t border-border p-4 bg-background-subtle">
          <ProjectDetails project={project} />
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: ProjectStatus }) {
  const labels: Record<ProjectStatus, string> = {
    on_track: 'On Track',
    at_risk: 'At Risk',
    late: 'Late',
  };

  const icons: Record<ProjectStatus, typeof Clock> = {
    on_track: Clock,
    at_risk: AlertTriangle,
    late: AlertCircle,
  };

  const Icon = icons[status];

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(status)}`}
    >
      <Icon className="h-3 w-3" />
      {labels[status]}
    </span>
  );
}

function VarianceIndicator({ days }: { days: number }) {
  const isLate = days < 0;
  const color = isLate ? 'text-danger' : days === 0 ? 'text-foreground-muted' : 'text-success';

  return (
    <div className={`flex items-center gap-2 min-w-[100px] ${color}`}>
      {isLate && <TrendingDown className="h-4 w-4" />}
      <span className="text-sm font-medium">{formatVariance(days)}</span>
    </div>
  );
}

function ProjectDetails({ project }: { project: Project }) {
  // Mock date change history - in production this would come from the API
  const dateChanges = generateMockDateChanges(project);

  return (
    <div className="space-y-4">
      {/* Key dates */}
      <div>
        <h4 className="text-sm font-medium mb-2">Schedule Details</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-foreground-muted">Earliest Promised</div>
            <div className="font-mono">{formatDate(project.earliestPromisedShipDate)}</div>
          </div>
          <div>
            <div className="text-foreground-muted">Latest Promised</div>
            <div className="font-mono">{formatDate(project.latestPromisedShipDate)}</div>
          </div>
          <div>
            <div className="text-foreground-muted">Expected (Best)</div>
            <div className="font-mono">
              {project.expectedShipDate ? formatDate(project.expectedShipDate) : '-'}
            </div>
          </div>
          <div>
            <div className="text-foreground-muted">Expected (Worst)</div>
            <div className="font-mono">
              {project.expectedShipDateWorst ? formatDate(project.expectedShipDateWorst) : '-'}
            </div>
          </div>
        </div>
      </div>

      {/* Date change history */}
      {dateChanges.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">Date Change History</h4>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background border-b border-border">
                  <th className="px-3 py-2 text-left font-medium text-foreground-muted">Date</th>
                  <th className="px-3 py-2 text-left font-medium text-foreground-muted">Previous</th>
                  <th className="px-3 py-2 text-left font-medium text-foreground-muted">New</th>
                  <th className="px-3 py-2 text-left font-medium text-foreground-muted">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {dateChanges.map((change, i) => (
                  <tr key={i} className="hover:bg-background">
                    <td className="px-3 py-2 text-foreground-muted">
                      {formatDate(change.changedAt)}
                    </td>
                    <td className="px-3 py-2 font-mono">{formatDate(change.previousDate)}</td>
                    <td className="px-3 py-2 font-mono">
                      <span
                        className={
                          new Date(change.newDate) > new Date(change.previousDate)
                            ? 'text-danger'
                            : 'text-success'
                        }
                      >
                        {formatDate(change.newDate)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <ReasonBadge reason={change.reasonCode} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

interface DateChange {
  changedAt: string;
  previousDate: string;
  newDate: string;
  reasonCode: string;
}

function generateMockDateChanges(project: Project): DateChange[] {
  // Only generate changes for at-risk or late projects
  if (project.status === 'on_track') return [];

  const reasons = [
    'parts_delayed',
    'priority_bumped',
    'capacity_constrained',
    'estimate_exceeded',
    'resource_unavailable',
  ];

  const numChanges = 1 + Math.floor(Math.random() * 3);
  const changes: DateChange[] = [];

  let currentDate = project.expectedShipDate ?? project.latestPromisedShipDate;

  for (let i = 0; i < numChanges; i++) {
    const daysAgo = 3 + i * 5 + Math.floor(Math.random() * 5);
    const changeDate = new Date();
    changeDate.setDate(changeDate.getDate() - daysAgo);

    const prevDate = new Date(currentDate);
    prevDate.setDate(prevDate.getDate() - (2 + Math.floor(Math.random() * 5)));

    changes.push({
      changedAt: format(changeDate, 'yyyy-MM-dd'),
      previousDate: format(prevDate, 'yyyy-MM-dd'),
      newDate: currentDate,
      reasonCode: reasons[Math.floor(Math.random() * reasons.length)] ?? 'manual_adjustment',
    });

    currentDate = format(prevDate, 'yyyy-MM-dd');
  }

  return changes;
}

function ReasonBadge({ reason }: { reason: string }) {
  const labels: Record<string, string> = {
    parts_delayed: 'Parts Delayed',
    priority_bumped: 'Priority Changed',
    capacity_constrained: 'Capacity Issue',
    estimate_exceeded: 'Estimate Exceeded',
    resource_unavailable: 'Resource Unavailable',
    eco_impact: 'ECO Impact',
    customer_request: 'Customer Request',
    manual_adjustment: 'Manual Adjustment',
  };

  const colors: Record<string, string> = {
    parts_delayed: 'bg-orange-100 text-orange-700',
    priority_bumped: 'bg-purple-100 text-purple-700',
    capacity_constrained: 'bg-red-100 text-red-700',
    estimate_exceeded: 'bg-yellow-100 text-yellow-700',
    resource_unavailable: 'bg-blue-100 text-blue-700',
    eco_impact: 'bg-indigo-100 text-indigo-700',
    customer_request: 'bg-green-100 text-green-700',
    manual_adjustment: 'bg-gray-100 text-gray-700',
  };

  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[reason] ?? colors.manual_adjustment}`}>
      {labels[reason] ?? reason}
    </span>
  );
}

function formatDate(dateString: string): string {
  try {
    return format(parseISO(dateString), 'MMM d, yyyy');
  } catch {
    return dateString;
  }
}

function AtRiskLoading() {
  return (
    <div className="space-y-6">
      <div>
        <div className="h-8 w-48 bg-background-muted rounded animate-pulse" />
        <div className="h-4 w-72 bg-background-muted rounded animate-pulse mt-2" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="border border-border rounded-lg p-4 bg-background-subtle">
            <div className="h-4 w-20 bg-background-muted rounded animate-pulse" />
            <div className="h-8 w-12 bg-background-muted rounded animate-pulse mt-2" />
          </div>
        ))}
      </div>
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="border border-border rounded-lg p-4">
            <div className="h-6 w-full bg-background-muted rounded animate-pulse" />
            <div className="h-4 w-48 bg-background-muted rounded animate-pulse mt-2" />
          </div>
        ))}
      </div>
    </div>
  );
}

function AtRiskError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="h-12 w-12 text-danger mb-4" />
      <h3 className="text-lg font-medium">Failed to load at-risk projects</h3>
      <p className="text-sm text-foreground-muted mt-1 max-w-md">{message}</p>
      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary-hover transition-colors text-sm font-medium"
      >
        Try Again
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center border border-border rounded-lg bg-success-subtle">
      <Clock className="h-12 w-12 text-success mb-4" />
      <h3 className="text-lg font-medium text-success-foreground">All projects on track</h3>
      <p className="text-sm text-success-foreground/70 mt-1">
        No projects are currently at risk or running late.
      </p>
    </div>
  );
}
